import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from "axios";
import { buildApiUrl } from "./apiConfig";

const AUTH_TOKEN_KEY = "authToken";
const AUTH_REJECTION_STATUSES = new Set([401, 403]);

// Crearea contextului de autentificare
const AuthContext = createContext();

// Exportarea unui hook pentru a folosi contextul de autentificare
export const useAuth = () => useContext(AuthContext);

const decodeJwtPayload = (token) => {
  try {
    const parts = String(token || "").split(".");
    if (parts.length !== 3) return null;

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(window.atob(padded));
  } catch (_) {
    return null;
  }
};

export const isAuthTokenValid = (token) => {
  if (!token || token === "undefined" || token === "null") {
    return false;
  }

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }

  return Number(payload.exp) * 1000 > Date.now();
};

export const getStoredAuthToken = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const token =
    window.localStorage.getItem(AUTH_TOKEN_KEY) ||
    window.sessionStorage.getItem(AUTH_TOKEN_KEY);

  if (!isAuthTokenValid(token)) {
    clearStoredAuthToken();
    return null;
  }

  return token;
};

export const hasStoredAuthToken = () => Boolean(getStoredAuthToken());

export const applyAuthHeader = (token = getStoredAuthToken()) => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
};

export const storeAuthToken = (token, { remember = false } = {}) => {
  if (!isAuthTokenValid(token)) {
    clearStoredAuthToken();
    return false;
  }

  const targetStorage = remember ? window.localStorage : window.sessionStorage;
  const otherStorage = remember ? window.sessionStorage : window.localStorage;
  targetStorage.setItem(AUTH_TOKEN_KEY, token);
  otherStorage.removeItem(AUTH_TOKEN_KEY);
  applyAuthHeader(token);
  return true;
};

export const clearStoredAuthToken = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
  applyAuthHeader(null);
};

const isAuthRejectionError = (error) =>
  AUTH_REJECTION_STATUSES.has(Number(error?.response?.status));

// Furnizorul de context de autentificare
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(hasStoredAuthToken);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const verifyStoredSession = async () => {
      const token = getStoredAuthToken();
      applyAuthHeader(token);

      if (!token) {
        if (isMounted) {
          setIsAuthenticated(false);
          setIsAuthReady(true);
        }
        return;
      }

      try {
        await axios.get(buildApiUrl("/auth/verify"));
        if (isMounted) {
          setIsAuthenticated(true);
        }
      } catch (error) {
        if (isAuthRejectionError(error) || !isAuthTokenValid(token)) {
          clearStoredAuthToken();
          if (isMounted) {
            setIsAuthenticated(false);
          }
        } else {
          applyAuthHeader(token);
          if (isMounted) {
            setIsAuthenticated(true);
          }
          console.warn(
            "[auth] Verificarea sesiunii a eșuat temporar; păstrăm tokenul local valid.",
            {
              status: error?.response?.status || null,
              message: error?.message || "unknown",
            }
          );
        }
      } finally {
        if (isMounted) {
          setIsAuthReady(true);
        }
      }
    };

    verifyStoredSession();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const token = getStoredAuthToken();
    const payload = decodeJwtPayload(token);
    const expiresAt = Number(payload?.exp || 0) * 1000;
    const delay = expiresAt - Date.now();

    if (!token || delay <= 0) {
      setIsAuthenticated(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      clearStoredAuthToken();
      setIsAuthenticated(false);
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [isAuthenticated]);

  const login = useCallback((token, options) => {
    if (!storeAuthToken(token, options)) {
      setIsAuthenticated(false);
      setIsAuthReady(true);
      return false;
    }

    setIsAuthenticated(true);
    setIsAuthReady(true);
    return true;
  }, []);

  const logout = useCallback(() => {
    clearStoredAuthToken();
    setIsAuthenticated(false);
    setIsAuthReady(true);
    axios.post(buildApiUrl("/logout")).catch(() => {});
  }, []);

  const value = useMemo(
    () => ({ isAuthenticated, isAuthReady, setIsAuthenticated, login, logout }),
    [isAuthenticated, isAuthReady, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
